import { Injectable } from '@angular/core';
import { delay, Observable, of } from 'rxjs';
import { HttpEvent, HttpRequest, HttpResponse } from '@angular/common/http';
import {
  NotificationChannel,
  NotificationHistoryItem,
  NotificationHistoryResponse,
  NotificationOutcome,
  NotificationTriggerType,
} from '../../models/notification-history.interface';

/**
 * Mock-mode handler for GET /:tenantId/notifications/history. Returns a paged,
 * newest-first list of plausible rows (a mix of SENT/SKIPPED/FAILED across both
 * trigger types and both channels, enriched with a customer name + reference)
 * so the Sent tab has something to render offline. Mirrors
 * ServiceDuesMocksService's generate-then-slice paging.
 */
@Injectable({ providedIn: 'root' })
export class NotificationHistoryMocksService {
  handleMockRequest(req: HttpRequest<any>): Observable<HttpEvent<any>> {
    const url = new URL(req.url);
    const page = parseInt(url.searchParams.get('page') || '1', 10);
    const limit = parseInt(url.searchParams.get('limit') || '50', 10);

    const allItems = this.generateMockData();
    const total = allItems.length;
    const data = allItems.slice((page - 1) * limit, page * limit);

    const res: NotificationHistoryResponse = {
      data,
      total,
      page,
      limit,
      totalPages: Math.ceil(total / limit),
    };
    return of(new HttpResponse({ status: 200, body: res })).pipe(delay(400));
  }

  private generateMockData(): NotificationHistoryItem[] {
    const triggers: NotificationTriggerType[] = ['WOF_SERVICE_DUE', 'GOOGLE_REVIEW_REQUEST'];
    const channels: NotificationChannel[] = ['EMAIL', 'SMS'];
    // Weighted so most rows are SENT, with a few SKIPPED/FAILED for colour.
    const outcomes: NotificationOutcome[] = ['SENT', 'SENT', 'SENT', 'SKIPPED', 'FAILED'];
    const skipReasons = ['NO_CONTACT_DETAILS', 'SMS_DISABLED', 'ALREADY_SENT', 'OPTED_OUT'];
    const names = [
      'Acme Motors Ltd',
      'Jane Smith',
      'Northline Freight',
      'Bob Wilson',
      'Coastal Cabs',
    ];

    const now = new Date();
    const items: NotificationHistoryItem[] = [];

    for (let i = 1; i <= 34; i++) {
      const trigger = triggers[i % triggers.length];
      const channel = channels[i % channels.length];
      const outcome = outcomes[i % outcomes.length];
      const isVehicle = trigger === 'WOF_SERVICE_DUE';

      // createdAt walks backwards from now so the raw list is newest-first.
      const createdAt = new Date(now.getTime() - i * 6 * 60 * 60 * 1000);
      const runDate = createdAt.toISOString().slice(0, 10);

      items.push({
        id: i + 1,
        runDate,
        createdAt: createdAt.toISOString(),
        triggerType: trigger,
        channel,
        outcome,
        skipReason: outcome === 'SKIPPED' ? skipReasons[i % skipReasons.length] : null,
        customerName: names[i % names.length],
        recipientRef:
          channel === 'EMAIL' ? `customer${i}@example.co.nz` : `021 ${String(i).padStart(7, '0')}`,
        reference: isVehicle ? `ABC${String(i).padStart(3, '0')}` : `INV-${1000 + i}`,
        referenceType: isVehicle ? 'vehicle' : 'invoice',
        messageSid:
          outcome === 'SENT' && channel === 'SMS' ? `SM${String(i).padStart(6, '0')}` : null,
      });
    }

    return items;
  }
}
